// Music for the real game. The composition root creates one engine (src/audio), tells it what the
// game is doing, and hands the HUD its control through a slot, so ui/ never imports audio/.
//
//   state.loading ─┐
//   state.leader ──┼─► screenOf ─┐
//   state.snapshot ─► atWarIn ───┴─► music.setState          (bed: theme, explore or tension)
//
//   actions.nextTurn ─► turnCue ─────► music.cue             (one sting a turn: war > deal > turn)
//   actions.openPanel('victory') ────► music.cue('victory-opened')
//
// Only explicit turns sting: autoplay calls the services' own nextTurn, not this wrapper, so a
// running time slider stays quiet. Sound starts on the first click or key press (src/audio).

import { effect } from '@preact/signals';
import { h, type ComponentChildren } from 'preact';
import { createMusic, type Music, type MusicEvent, type Screen } from '../audio/index.ts';
import { MusicControl } from '../audio/MusicControl.tsx';
import type { Services } from '../services/api.ts';

/** What the wiring needs from the engine (tests pass a spy). */
export type MusicPort = Pick<Music, 'setState' | 'cue'>;

/** What a turn did, as far as the sting goes. */
export interface TurnFacts {
  warBefore: boolean;
  warAfter: boolean;
  dealSigned: boolean;
}

const WAR = 'war';
const VICTORY_PANEL = 'victory';

/** The engine and the HUD control that drives it (compact: the top bars are full). */
export function createAppMusic(): { music: Music; control: ComponentChildren } {
  const music = createMusic();
  return { music, control: h(MusicControl, { music, layout: 'compact' }) };
}

export function screenOf(loading: boolean, leader: string | null): Screen {
  if (loading) {
    return 'loading';
  }
  return leader === null ? 'leader-select' : 'game';
}

/** True when any actor is at war with the snapshot's viewer. */
export function atWarIn(relations: ReadonlyMap<number, { relation: string }> | null | undefined): boolean {
  if (!relations) {
    return false;
  }
  for (const info of relations.values()) {
    if (info.relation === WAR) {
      return true;
    }
  }
  return false;
}

/** The one sting a turn plays: a new war over a signed deal over the plain turn chime. */
export function turnCue(facts: TurnFacts): MusicEvent {
  if (facts.warAfter && !facts.warBefore) {
    return 'war-declared';
  }
  return facts.dealSigned ? 'deal-signed' : 'turn-advanced';
}

function warNow(services: Services): boolean {
  return atWarIn(services.state.snapshot.peek()?.relations);
}

/** `services` with the turn and victory-screen actions sounding their stings. */
export function withRealMusic(services: Services, music: MusicPort): Services {
  const { actions, state } = services;

  const nextTurn = async (): Promise<void> => {
    const warBefore = warNow(services);
    await actions.nextTurn();
    music.cue(turnCue({ warBefore, warAfter: warNow(services), dealSigned: false }));
  };

  const openPanel: Services['actions']['openPanel'] = (panel, arg) => {
    const opening = panel === VICTORY_PANEL && state.panel.peek() !== VICTORY_PANEL;
    actions.openPanel(panel, arg);
    if (opening) {
      music.cue('victory-opened');
    }
  };

  return { ...services, actions: { ...actions, nextTurn, openPanel } };
}

/** Keep the engine's screen and war state in step with the app; returns the unsubscribe. */
export function bindRealMusic(services: Services, music: MusicPort): () => void {
  const { state } = services;
  return effect(() => {
    const screen = screenOf(state.loading.value !== null, state.leader.value);
    music.setState({ screen, atWar: atWarIn(state.snapshot.value?.relations) });
  });
}
